import { oddsPageAtom } from '@/atoms/odds-page';
import { OddsButton, OddsStatus } from '@/components/widgets/odds-button';
import { TemplateOddsButton } from '@/components/widgets/odds-button/template-odds-button';
import { useGetBetTypeSetting, useGetChoiceTitle, useGetGoal } from '@/hooks';
import { useSiteStore } from '@/store/site';
import { SportMatchModel, Market, StrongTeam } from '@nf/types/odds';
import { IO, MaybeMatch, matchEquals } from '@nf/utils-common/fpw/monadic';
import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { Fragment, useEffect, useState } from 'react';
import { matchStyles } from '../../match/match.style';
import { newbieMatchStyle } from '../newbie-match.style';
import { itemsEmptyCheck } from '@nf/utils-common/fpw/utils';
import { useLiveTimer } from '@/features/odds-page/hooks/use-live-timer';
import { matchService } from '@/services/match.service';
import { scoreboardService } from '@/services/scoreboard.service';
import { matchSpecialStyles } from '../../match-special/match-special.style';
import { useOddsTableMarsContext } from '@/features/odds-table-mars/context';
import { useRouter } from '@/navigation';
import { Breakpoint } from '@/features/breakpoint';
import { useBreakpoint } from '@/features/breakpoint/contexts';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { KafkaNote } from '@nf/utils-common/constants';
import { useGoldenDuelSetting } from '@/hooks/query/golden-duel';

interface NewbieMatchEventProps {
	match: SportMatchModel;
	markets: Market[];
	homeTeamName: string;
	awayTeamName: string;
	isLoading: boolean,
	teamAScore?: string;
	teamBScore?: string;
}
export const NewbieMatchEvent = ({
	match,
	markets,
	homeTeamName,
	awayTeamName,
	isLoading,
	teamAScore,
	teamBScore
}: NewbieMatchEventProps) => {
	const { GameID, MaT, LeagueId, MatchId } = match;
	const router = useRouter();
	const { sport, oddsType, actStatus } = useOddsTableMarsContext();
	const oddsData = useAtomValue(oddsPageAtom);
	const betTypeFilterCriterion = useSiteStore(state => state.getBetTypeFilterCriterion(sport));
	const betTypes = betTypeFilterCriterion?.betTypes || [];
	const betType = GameID === 8
		? (markets.some(x => x.BetTypeId === betTypes[0]) ? betTypes[0] : betTypes[1]) || 0
		: betTypes[0] || 0;

	const getBetTypeSetting = useGetBetTypeSetting();
	const getChoiceTitle = useGetChoiceTitle(betType);
	const betTypeSetting = getBetTypeSetting(betType, sport);
	const sortBetteams = ['1', '2', 'x'];
	const choices = [5, 15].includes(betType) ? betTypeSetting.betChoices.sort((a, b) => sortBetteams.indexOf(a.betTeam) - sortBetteams.indexOf(b.betTeam)) : betTypeSetting.betChoices;
	const [market, setMarket] = useState<Market>();
	const view = useAppearanceStore(state => state.view);
	const { breakpoint } = useBreakpoint();
	const getGoal = useGetGoal();
	const { } = useLiveTimer(match);
	const { isGoldenDuelSupported } = useGoldenDuelSetting();

	useEffect(() => {
		MaybeMatch(!isLoading)
			.chain(() => itemsEmptyCheck(markets))
			.fold(
				() => { },
				() => IO.of(markets)
					.map((markets: Market[]) => setMarket(markets.find(market => market?.BetTypeId === betType)))
					.run()
			)
	}, [isLoading, betType, markets])

	const serveTeam = scoreboardService.getServeTeam(match, sport);
	const strongTeam = matchService.getStrongTeam(markets);
	const isHomeStrong = strongTeam === StrongTeam.Home;
	const isAwayStrong = strongTeam === StrongTeam.Away;

	const setKafka = () => {
		if (view == ViewEnum.mars && breakpoint <= Breakpoint.sm) {
			setKafkaNormal({
				note: KafkaNote.MarsV1,
				type: 6,
				action: 34
			});
		}
	}

	const setPromotionKafka = () => {
		if (isGoldenDuelSupported(GameID, LeagueId)) {
			setKafkaNormal({
				note: KafkaNote.GoldenDuel,
				type: 1,
				action: 45
			});
		}
	}

	const handleVisitSingleMatch = () => {
		setKafka();
		setPromotionKafka();
		router.push(`/sports/${GameID}/${MaT}/${LeagueId}/${MatchId}`);
	}
	return (
		<div className={newbieMatchStyle.event}>
			{
				choices?.map((betChoice, index) => {
					const teamClassName = matchEquals(sport)
						.on(44, () => index == 0 ? matchStyles.teamRed : matchStyles.teamBlue)
						.otherwise(() => (index === 0 ? isHomeStrong : isAwayStrong) ? matchStyles.teamStrong : '')
					const disableScoreClassName = matchEquals(sport)
						.on(50, () => (index === 0 ? '1' : '2') === serveTeam ? '' : matchSpecialStyles.scoreDisabled)
						.otherwise(() => '')
					return (
						<Fragment key={betChoice.betTeam}>
							<div className={newbieMatchStyle.team} onClick={handleVisitSingleMatch}>
								<div className={clsx('multi-line-ellipsis', index < 2 && teamClassName)}>
									{
										matchEquals(index)
											.on(0, () => homeTeamName)
											.on(1, () => awayTeamName)
											.otherwise(() => getChoiceTitle({
												choice: betChoice, market: market, match, teamMap: oddsData?.teamNameMap
											}))
									}
								</div>
								<div className={clsx(newbieMatchStyle.score, disableScoreClassName)}>
									{
										match.IsLive && matchEquals(index)
											.on(0, () => teamAScore)
											.on(1, () => teamBScore)
											.otherwise(() => '')
									}
								</div>
							</div>
							<div className={newbieMatchStyle.bet}>
								{
									market?.Selections[betChoice.betTeam] ? (
										<OddsButton
											key={`${market.MatchId}_${market.MarketId}_${market.BetTypeId}_${betChoice.betTeam}`}
											goal={[5, 15, 20, 501, 8828].includes(market.BetTypeId) ? '' : getGoal(market, betChoice)}
											match={match}
											market={market}
											selection={market.Selections[betChoice.betTeam]}
											homeTeamName={homeTeamName}
											awayTeamName={awayTeamName}
											variant={'vertical'}
											betTeam={market.Selections[betChoice.betTeam].SelId}
											originalPrice={market.Selections[betChoice.betTeam].Price}
											sport={sport}
											type="OU"
											oddsType={oddsType}
											accountStatus={actStatus ?? 0}
										/>
									) : (
										<TemplateOddsButton
											oddsStatus={OddsStatus.Close}
											isSelected={false}
											price={''}
											isShowPrice={false}
											variant={'vertical'}
										/>
									)
								}

							</div>
						</Fragment>
					)
				})
			}
		</div>
	)
}